import { useEffect, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { LoadingOverlay, toast } from '@/components';
import type { Profile } from '@/models/profile';
import OTPVerificationView from '@/pages/components/OTPVerificationView';
import { BookingService } from '@/services/bookingService';
import { getLiffProfile } from '@/services/liffService';
import { ProfileService } from '@/services/profileService';

interface OtpNavState {
  ref?: string;
  phoneNumber?: string;
  telPrefix?: string;
  tel?: string;
  devOtpCode?: string;
  expiresAt?: Date;
  intent?: 'saveProfile';
  profile?: Profile;
}

const OTPVerificationPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as OtpNavState;

  const [otpRef, setOtpRef] = useState<string>(state.ref ?? '');
  const [phoneNumber, setPhoneNumber] = useState<string>(
    state.phoneNumber ?? '',
  );
  const [expiresAt, setExpiresAt] = useState<Date | null>(
    state.expiresAt ?? null,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!state.ref || !state.phoneNumber) {
      void navigate('/profile/phone');
      return;
    }
    setOtpRef(state.ref);
    setPhoneNumber(state.phoneNumber);
    setExpiresAt(state.expiresAt ?? null);
  }, [navigate, state.expiresAt, state.phoneNumber, state.ref]);

  const getLastThreeDigits = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.slice(-3);
  };

  const submitOTP = async (code: string) => {
    if (!otpRef) {
      void navigate('/profile/phone');
      return false;
    }

    setErrorKey(null);

    const success = await BookingService.validateOTP(code, otpRef, phoneNumber);
    if (!success) {
      return false;
    }

    if (state.intent === 'saveProfile' && state.profile) {
      try {
        const profile = state.profile;

        if (ProfileService.isLoggedIn()) {
          await ProfileService.updateMe({
            name: profile.name,
            email: profile.email,
            telPrefix: profile.telPrefix,
            tel: profile.tel,
            nationality: profile.nationalityCode,
            birthday: profile.birthday,
            gender: profile.gender,
          });

          toast.success(t('profile.settings.toast.updated'));
          void navigate('/profile/settings', { replace: true });
          return true;
        }

        const liffProfile = await getLiffProfile();
        await ProfileService.signup({
          name: profile.name,
          ref: otpRef,
          email: profile.email,
          telPrefix: profile.telPrefix,
          tel: profile.tel,
          nationality: profile.nationalityCode,
          birthday: profile.birthday,
          gender: profile.gender,
          lineUserId: liffProfile?.userId ?? 'web',
        });

        toast.success(t('profile.settings.toast.signedUp'));
        void navigate('/profile/home', { state: { phoneNumber } });
        return true;
      } catch (e) {
        console.error('Failed to save profile after OTP verification:', e);
        setErrorKey('profile.otp.errors.saveProfileFailed');
        return true;
      }
    }

    // After OTP is verified, login to obtain access token + user.
    // The API client persists the token in localStorage automatically.
    try {
      const liffProfile = await getLiffProfile();

      if (!state.telPrefix || !state.tel) {
        throw new Error('Missing telPrefix/tel in OTP navigation state');
      }

      await ProfileService.login({
        telPrefix: state.telPrefix,
        tel: state.tel,
        ref: otpRef,
        lineUserId: liffProfile?.userId ?? 'web',
      });
    } catch (e) {
      console.error('Failed to login after OTP verification:', e);
      setErrorKey('profile.otp.errors.verifyFailed');
      return true;
    }

    navigate('/profile/home', {
      replace: true,
      state: { phoneNumber, showLoggedInToast: true },
    });
    return true;
  };

  const resendOTP = async () => {
    if (!phoneNumber) {
      void navigate('/profile/phone');
      return;
    }

    setErrorKey(null);

    const resp = await BookingService.requestOTP(phoneNumber, 'phoneNumber');
    setOtpRef(resp.ref);
    setExpiresAt(resp.expiresAt);
  };

  return (
    <div className="mx-auto max-w-md space-y-6">
      <OTPVerificationView
        illustrationSrc="/illustrations/otp-think.png"
        title={t('profile.otp.title')}
        description={
          phoneNumber ? (
            <>
              {t('profile.otp.description')}
              <br />
              {t('package.otp.sentTo', {
                last3: getLastThreeDigits(phoneNumber),
              })}
            </>
          ) : (
            t('profile.otp.description')
          )
        }
        refCode={otpRef}
        expiresAt={expiresAt}
        submitOTP={submitOTP}
        resendOTP={resendOTP}
        onLoading={setIsLoading}
      />

      {errorKey && (
        <p className="text-error text-sm leading-5">{t(errorKey)}</p>
      )}

      <LoadingOverlay isVisible={isLoading} />
    </div>
  );
};

export default OTPVerificationPage;
